<template>
  <div class="as-coach-inspect-workspace">
    <v-overlay
      v-if="asDrawer"
      :value="inspect.showInspectChecklistView"
      :z-index="89"
      class="as-coach-inspect-workspace__scrim"
      data-testid="coach-inspect-scrim"
      @click="closeDrawer"
    />

    <component
      :is="asDrawer ? 'v-navigation-drawer' : 'div'"
      v-if="asDrawer || inspect.showInspectChecklistView"
      v-bind="asDrawer ? drawerBindings : {}"
      :class="{ 'as-coach-inspect-workspace__drawer': asDrawer }"
      :data-testid="asDrawer ? 'coach-inspect-drawer' : undefined"
    >
      <AsCoachInspectChecklistCard
        v-if="inspect.showInspectChecklistView"
        :show-breadcrumbs="!asDrawer"
        :breadcrumbs="breadcrumbs"
        :employee-name="inspect.selectedEmployee?.fullName"
        :first-name="inspect.selectedEmployee?.first_name"
        :last-name="inspect.selectedEmployee?.last_name"
        :job-title="inspect.inspectChecklistJobTitle"
        :avatar="inspect.selectedEmployee?.avatar"
        :primary-position-name="inspect.coachPrimaryPositionName"
        :preparing-for-position-name="inspect.coachPreparingForPositionName"
        :responsibility="inspect.inspectChecklistResponsibility"
        :levels="inspect.coachChecklistLevels"
        :pass-filter="inspect.coachChecklistPassFilter"
        :search-query="inspect.coachChecklistSearchQuery"
        @breadcrumb-click="emit('breadcrumb-click', $event)"
        @update:passFilter="inspect.onCoachChecklistPassFilterUpdate"
        @pass-filter-change="inspect.onCoachChecklistPassFilterChange"
        @update:searchQuery="inspect.setInspectField('coachChecklistSearchQuery', $event)"
        @search="inspect.setInspectField('coachChecklistSearchQuery', $event)"
        @toggle-level="inspect.handleCoachChecklistToggleLevel"
        @badge-click="inspect.handleCoachChecklistBadgeClick"
        @badge-remove="inspect.handleCoachChecklistBadgeRemove"
        @expiration-synced="inspect.handleCoachChecklistBadgeExpirationSynced"
        @action-click="inspect.handleCoachChecklistActionClick"
        @secondary-action-click="inspect.handleCoachChecklistSecondaryActionClick"
        @badge-chatter="inspect.handleBadgeChatter"
      >
        <template v-if="asDrawer" #top-actions>
          <v-btn
            icon
            class="as-coach-inspect-workspace__close"
            aria-label="Close inspect drawer"
            data-testid="coach-inspect-close"
            @click="closeDrawer"
          >
            <v-icon color="#475467">mdi-close</v-icon>
          </v-btn>
        </template>
      </AsCoachInspectChecklistCard>
    </component>

    <GiveBadgeDialog
      :value="inspect.showEmployeeModal"
      :employeeName="inspect.selectedEmployee?.fullName"
      :responsibilityName="inspect.selectedResponsibility?.name"
      :canNotify="inspect.coachNotifyEligible"
      :accessNeedsVerification="inspect.coachAccessNeedsVerification"
      :currentBadgeLevel="inspect.currentBadgeLevel"
      :lockedLevel="inspect.giveBadgeLockedLevel"
      :description="inspect.giveBadgeDescription"
      @input="inspect.setInspectField('showEmployeeModal', $event)"
      @give-badge="inspect.handleGiveBadge($event)"
      @close="inspect.closeEmployeeModal"
    />
    <UpgradeBadgeDialog
      :value="inspect.showUpgradeBadgeDialog"
      :employeeName="inspect.selectedEmployee?.fullName"
      :responsibilityName="inspect.selectedResponsibility?.name"
      :canNotify="inspect.coachNotifyEligible"
      :accessNeedsVerification="inspect.coachAccessNeedsVerification"
      :currentBadgeLevel="inspect.currentBadgeLevelForUpgrade"
      @input="inspect.setInspectField('showUpgradeBadgeDialog', $event)"
      @give-badge="inspect.handleGiveBadge($event)"
      @close="inspect.closeUpgradeBadgeModal"
    />
    <RemoveBadgeDialog
      :value="inspect.showRemoveBadgeDialog"
      :badgeName="
        inspect.selectedBadgeForRemoval?.badgeName || inspect.selectedBadgeForRemoval?.level || ''
      "
      :responsibilityName="
        inspect.selectedBadgeForRemoval?.responsibilityName ||
        inspect.selectedBadgeForRemoval?.responsibility?.name ||
        ''
      "
      :employeeName="
        inspect.selectedEmployee?.fullName || inspect.selectedRowData?.user?.name || ''
      "
      @input="inspect.setInspectField('showRemoveBadgeDialog', $event)"
      @remove="inspect.handleConfirmRemoveBadge"
      @close="inspect.handleCloseRemoveBadgeDialog"
    />

    <v-navigation-drawer
      :value="inspect.showCoachCheckpointPanel"
      right
      app
      temporary
      :width="inspect.coachCheckpointPanelWidth"
      class="coach-checkpoint-panel-drawer"
      :class="{ 'as-coach-inspect-workspace__checkpoint-drawer': asDrawer }"
      @input="onCheckpointDrawerInput"
    >
      <CheckpointInspectionRightPane
        v-if="inspect.coachPanelCheckpointMerged"
        :employee-name="inspect.selectedEmployee?.fullName"
        :first-name="inspect.selectedEmployee?.first_name"
        :last-name="inspect.selectedEmployee?.last_name"
        :job-title="inspect.selectedRowData?.user?.position || ''"
        :responsibility-name="inspect.selectedResponsibility?.name || ''"
        :avatar="inspect.selectedEmployee?.avatar"
        :level="inspect.coachPanelLevel"
        :responsibility-id="inspect.selectedResponsibility?.id"
        :employee-id="inspect.selectedEmployee?.id"
        :reinspect-mode="inspect.coachPanelActionId === 're-inspect'"
        :checkpoint-item="inspect.coachPanelCheckpointMerged"
        @close="onCheckpointPanelClose"
        @evaluated="inspect.handleCoachPanelEvaluated"
        @onClose="inspect.handleCoachPanelReset"
      />
    </v-navigation-drawer>

    <ReasonModal
      :show-dialog="inspect.showCoachMarkNaModal"
      title="Mark Criteria as Not Applicable"
      description="Provide a reason why this criteria is not applicable."
      place-holder="Explain why the criteria is not applicable."
      btn-label="Mark as Not Applicable"
      :btn-danger="false"
      btn-color="#666666"
      :value="inspect.coachMarkNaReason"
      @input="inspect.setInspectField('coachMarkNaReason', $event)"
      @onAction="inspect.confirmCoachMarkNa"
      @onClose="inspect.closeCoachMarkNaModal"
    />

    <AwardBadgeDialog
      :isVisible="inspect.showCoachAwardBadgeDialog"
      :employee="{
        name: inspect.selectedEmployee?.fullName ?? '',
        avatar: inspect.selectedEmployee?.avatar ?? '',
      }"
      :level="inspect.coachAwardBadgeLevel"
      :currentEmployee="inspect.currentEmployeeForAwardDialog"
      :eligibility-by-access-null="true"
      :use-verification-notify-copy="true"
      :responsibilityName="inspect.selectedResponsibility?.name ?? ''"
      @close="inspect.closeCoachAwardBadgeDialog"
      @skip="inspect.closeCoachAwardBadgeDialog"
      @activate="inspect.handleCoachAwardBadgeActivate"
    />

    <ChatterDrawer
      :drawer="inspect.showChatterDrawer"
      :type="'Badge'"
      :typeValue="inspect.chatterTypeValue || ''"
      :listData="inspect.selectedBadgeChatter"
      :object-type="inspect.chatterType || 'badge'"
      :object-id="inspect.selectedBadgeChatter?.id ?? null"
      :iconName="inspect.chatterIconName || 'org_icon_responsibility'"
      :chatterList="inspect.chatterData"
      :totalCheckpointChatter="inspect.totalChatter"
      @closeDrawer="inspect.closeChatterDrawer"
      @requestMoreCheckpointsChatter="inspect.loadMoreChatter"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import AsCoachInspectChecklistCard from "@components/AsCoachInspectChecklistCard/AsCoachInspectChecklistCard.vue";
import CheckpointInspectionRightPane from "@components/CheckpointInspectionRightPane/CheckpointInspectionRightPane.vue";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import GiveBadgeDialog from "@components/GiveBadgeDialog/GiveBadgeDialog.vue";
import UpgradeBadgeDialog from "@components/UpgradeBadgeDialog/UpgradeBadgeDialog.vue";
import RemoveBadgeDialog from "@components/RemoveBadgeDialog/RemoveBadgeDialog.vue";
import ReasonModal from "@components/ReasonModal/ReasonModal.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";

const props = defineProps({
  /** Reactive object returned by `useCoachInspect` (refs unwrapped). Read-only: write through its handlers. */
  inspect: {
    type: Object,
    required: true,
  },
  /** Host the checklist in a right temporary drawer instead of inline. */
  asDrawer: {
    type: Boolean,
    default: false,
  },
  /** Breadcrumb trail for the inline card; hidden when `asDrawer` is set. */
  breadcrumbs: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["breadcrumb-click", "close"]);

// Stateless: the checkpoint drawer is a sibling, so clicks inside it would count as
// "outside" and close this drawer. The scrim and close button close it instead.
const drawerBindings = computed(() => ({
  value: props.inspect.showInspectChecklistView,
  right: true,
  app: true,
  temporary: true,
  stateless: true,
  hideOverlay: true,
  width: "80%",
}));

function closeDrawer() {
  props.inspect.closeInspectChecklistView();
  emit("close");
}

let pendingReload = null;

// The drawer also emits `input` when closed programmatically, so coalesce overlapping reloads.
function reloadChecklist() {
  if (!props.asDrawer || !props.inspect.showInspectChecklistView) return null;
  if (!pendingReload) {
    pendingReload = props.inspect.reloadCoachChecklistAndBadge().finally(() => {
      pendingReload = null;
    });
  }
  return pendingReload;
}

async function onCheckpointPanelClose() {
  await props.inspect.closeCoachCheckpointPanel();
  await reloadChecklist();
}

async function onCheckpointDrawerInput(open) {
  props.inspect.setInspectField("showCoachCheckpointPanel", open);
  if (!open) await reloadChecklist();
}
</script>

<style scoped>
.as-coach-inspect-workspace__drawer.v-navigation-drawer {
  z-index: 90;
}

.as-coach-inspect-workspace__checkpoint-drawer.v-navigation-drawer {
  z-index: 95;
}

.as-coach-inspect-workspace__close {
  flex: 0 0 auto;
}
</style>
